import type { CSSProperties, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { classNames } from "@circle-react/helpers/twMergeWithCN";

interface MarqueeProps {
  children: ReactNode;
  className?: string;
  speed?: number; // Viewport widths per minute (decimals allowed, e.g., 0.5 for slow speed)
}

/**
 * Marquee Component
 *
 * Creates an infinite horizontal scrolling effect for its children.
 * Automatically duplicates content to ensure seamless looping.
 *
 * @param children - The content to scroll
 * @param className - Optional CSS classes for the container
 * @param speed - Scroll speed in viewport widths per minute (default: 10, decimals allowed for slower speeds)
 */
export const Marquee = ({
  children,
  className = "",
  speed = 10,
}: MarqueeProps) => {
  // ========== State Management ==========
  // Track the actual width of the content to calculate animation duration
  const [marqueeWidth, setMarqueeWidth] = useState(0);

  // How many times to duplicate content to fill the container
  const [multiplier, setMultiplier] = useState(1);

  // Ensure measurements happen after component mounts
  const [isMounted, setIsMounted] = useState(false);

  // Track viewport width for speed calculations
  const [viewportWidth, setViewportWidth] = useState(0);

  // ========== Refs for DOM Measurements ==========
  const containerRef = useRef<HTMLDivElement>(null);
  const marqueeRef = useRef<HTMLDivElement>(null);

  // ========== Width Calculation Logic ==========
  const calculateWidth = useCallback(() => {
    // Ensure both refs are available
    if (!marqueeRef.current || !containerRef.current) return;

    // Get the dimensions of container and content
    const containerRect = containerRef.current.getBoundingClientRect();
    const marqueeRect = marqueeRef.current.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const contentWidth = marqueeRect.width;

    // Calculate how many copies we need to fill the container
    if (containerWidth && contentWidth) {
      // If content is smaller than container, duplicate it enough times
      // Add 1 extra copy to ensure seamless loop
      const copiesNeeded =
        contentWidth < containerWidth
          ? Math.ceil(containerWidth / contentWidth) + 1
          : 1;

      setMultiplier(copiesNeeded);
    } else {
      setMultiplier(1);
    }

    // Store the width for animation duration calculation
    setMarqueeWidth(contentWidth);
  }, [marqueeRef, containerRef]);

  // ========== Viewport Width Tracking ==========
  useEffect(() => {
    const updateViewportWidth = () => {
      setViewportWidth(window.innerWidth);
    };

    // Initial set
    updateViewportWidth();

    // Update on resize
    window.addEventListener("resize", updateViewportWidth);
    return () => window.removeEventListener("resize", updateViewportWidth);
  }, []);

  // ========== ResizeObserver Setup ==========
  // Recalculate when container or content size changes
  useEffect(() => {
    if (!isMounted) return;

    // Initial calculation
    calculateWidth();

    // Early return if refs aren't ready
    if (!marqueeRef.current || !containerRef.current) return;

    // Watch for size changes
    const resizeObserver = new ResizeObserver(() => calculateWidth());
    resizeObserver.observe(containerRef.current);
    resizeObserver.observe(marqueeRef.current);

    // Cleanup
    return () => {
      resizeObserver.disconnect();
    };
  }, [calculateWidth, isMounted]);

  // ========== Mount Detection ==========
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // ========== Animation Duration Calculation ==========
  // Calculate duration based on viewport width for consistent visual speed
  const duration = useMemo(() => {
    if (marqueeWidth && speed && viewportWidth > 0) {
      // Convert speed from viewport widths per minute to seconds for one content width
      // Formula: (content width / viewport width) * (60 seconds / speed)
      const viewportWidthsPerContent = marqueeWidth / viewportWidth;
      const calculatedDuration = (viewportWidthsPerContent * 60) / speed;

      // Ensure minimum 1 second to avoid too-fast animations
      return Math.max(calculatedDuration, 1);
    }
    // Fallback duration
    return 10;
  }, [marqueeWidth, speed, viewportWidth]);

  // ========== Animation Styles ==========
  const marqueeStyle = useMemo(
    () =>
      ({
        animation: `marquee ${duration}s linear infinite`,
      }) as CSSProperties,
    [duration],
  );

  // ========== Helper Function to Render Duplicates ==========
  const renderChildren = useCallback(
    (count: number) =>
      Array.from({ length: count }, (_, index) => (
        <div key={index} className="flex shrink-0 items-center">
          {children}
        </div>
      )),
    [children],
  );

  // Don't render until mounted to avoid hydration issues
  if (!isMounted) return null;

  return (
    <>
      {/* Inject keyframes for the marquee animation */}
      <style>
        {`
          @keyframes marquee {
            0% { transform: translateX(0%); }
            100% { transform: translateX(-100%); }
          }
        `}
      </style>

      {/* Main container */}
      <div
        ref={containerRef}
        className={classNames("relative overflow-hidden", className)}
      >
        {/* Wrapper for both marquee tracks */}
        <div className="flex">
          {/* Primary marquee track */}
          <div
            className="marquee-content flex shrink-0 items-center"
            style={marqueeStyle}
          >
            {/* Original content with ref for measurement */}
            <div ref={marqueeRef} className="flex shrink-0 items-center">
              {children}
            </div>
            {/* Duplicated content based on multiplier */}
            {renderChildren(multiplier)}
          </div>

          {/* Secondary marquee track for seamless loop */}
          <div
            className="marquee-content flex shrink-0 items-center"
            style={marqueeStyle}
            aria-hidden // Hidden from screen readers as it's duplicate content
          >
            {/* Same content structure as primary track */}
            <div className="flex shrink-0 items-center">{children}</div>
            {renderChildren(multiplier)}
          </div>
        </div>
      </div>
    </>
  );
};
